import { handleActions } from "redux-actions";
import {
  getDateRanges,
  getRangeOptions,
  getRangeOptionsWithCustom,
  getSelectedRangeOptionWithCustom,
  getThisYear
} from "../../../../utils/date-range-options";
import {
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_FILTERS_SUCCESS,
  FETCH_CHART_SUCCESS,
  SET_SECONDARY_CATEGORIES_LOADING,
  SET_PRIMARY_CATEGORIES_LOADING,
  SET_PRICING_OPTIONS_LOADING,
  SET_FILTERS_DIRTY,
  SET_CHART_STATUS,
  SET_LOADING,
  SET_DATES,
  UPDATE_SECONDARY_CATEGORY_SELECTED,
  UPDATE_PRIMARY_CATEGORY_SELECTED,
  UPDATE_ONLY_CHALLENGERS_SELECTED,
  UPDATE_PRICING_OPTION_SELECTED,
  UPDATE_TABLE_VIEW_SELECTED,
  UPDATE_FILTERS_SUCCESS,
  UPDATE_PERIOD_TYPE,
  UPDATE_CUSTOM_RANGE,
  UPDATE_GROUP_BY,
  UPDATE_DATES,
  RESET_FILTER_SELECTED,
  UPDATE_EXCLUDE_MEMBERSHIP_SELECTED,
  UPDATE_EXCLUDE_ZERO_SESSIONS_SELECTED
} from "./packagesSalesSaga";

function secondaryCategory(useSecondaryCategories) {
  return { id: "secondary", name: `${useSecondaryCategories ? "Secondary" : ""} Category` };
}
export const initialState = {
  currentUser: {},
  chartData: {},
  initialRequest: false,
  isLoading: true,
  primaryCategoriesLoading: false,
  secondaryCategoriesLoading: false,
  pricingOptionsLoading: false,
  filtersDirty: false,
  selectedRequestDates: {},
  datesDirty: false,
  filters: {
    primary_categories: [],
    secondary_categories: [],
    pricing_options: [],
    toggles: {
      onlyChallengers: {}
    }
  },
  selectedFilters: {
    period_type: "Monthly",
    group_by: {},
    table_view: false,
    only_challengers: false,
    exclude_membership: true,
    exclude_zero_sessions: true,
    primary_categories: [],
    secondary_categories: [],
    pricing_options: []
  },
  periodTypeOptions: ["Daily", "Monthly"],
  groupBy: [],
  selectedRangeOption: {
    id: 1,
    name: getThisYear(undefined, true)
  },
  rangeOptions: getRangeOptions(undefined, true),
  chartInfo: {
    status: "idle",
    error: ""
  }
};

export default handleActions(
  {
    [SET_LOADING]: (state, action) => ({
      ...state,
      isLoading: action.payload
    }),
    [SET_PRIMARY_CATEGORIES_LOADING]: (state, action) => ({
      ...state,
      primaryCategoriesLoading: action.payload
    }),
    [SET_SECONDARY_CATEGORIES_LOADING]: (state, action) => ({
      ...state,
      secondaryCategoriesLoading: action.payload
    }),
    [SET_PRICING_OPTIONS_LOADING]: (state, action) => ({
      ...state,
      pricingOptionsLoading: action.payload
    }),
    [SET_FILTERS_DIRTY]: (state, action) => ({
      ...state,
      filtersDirty: action.payload
    }),
    [SET_DATES]: (state, action) => {
      const rangeOptions = getRangeOptions(
        action.payload.endDate,
        state.currentUser.theBeginningOfTime
      );
      return {
        ...state,
        selectedRequestDates: action.payload,
        rangeOptions: getRangeOptionsWithCustom(rangeOptions, action),
        selectedRangeOption: getSelectedRangeOptionWithCustom(action)
      };
    },
    [UPDATE_CUSTOM_RANGE]: (state, action) => ({
      ...state,
      selectedRangeOption: action.payload,
      selectedRequestDates: getDateRanges(
        state.currentUser.calendarEndDate,
        action.payload,
        state.currentUser.theBeginningOfTime
      ),
      filtersDirty: true,
      datesDirty: true
    }),
    [UPDATE_GROUP_BY]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        group_by: action.payload
      }
    }),
    [UPDATE_DATES]: (state, action) => ({
      ...state,
      selectedRequestDates: action.payload || state.selectedRequestDates,
      rangeOptions: getRangeOptionsWithCustom(state.rangeOptions, action),
      selectedRangeOption: getSelectedRangeOptionWithCustom(action),
      filtersDirty: true,
      datesDirty: true,
      selectedFilters: {
        ...state.selectedFilters
      }
    }),
    [FETCH_CURRENT_USER_SUCCESS]: (state, action) => ({
      ...state,
      currentUser: action.payload || state.currentUser,
      initialRequest: true
    }),
    [FETCH_CHART_SUCCESS]: (state, action) => ({
      ...state,
      chartData: action.payload || state.chartData
    }),
    [FETCH_FILTERS_SUCCESS]: (state, action) => {
      const useSecondaryCategories = state.currentUser.useSecondaryCategories;
      const secondary = secondaryCategory(useSecondaryCategories);

      return {
        ...state,
        filters: action.payload || state.filters,
        selectedFilters: {
          ...state.selectedFilters,
          only_challengers: action.payload.toggles.onlyChallengers.default ?? false,
          group_by: secondary
        },
        groupBy: [
          ...(useSecondaryCategories ? [{ id: "primary", name: "Primary Category" }] : []),
          secondary,
          { id: "pricing_option", name: "Sales Package" }
        ]
      };
    },
    [UPDATE_FILTERS_SUCCESS]: (state, action) => ({
      ...state,
      filters: action.payload
    }),
    [RESET_FILTER_SELECTED]: (state) => ({
      ...state,
      filtersDirty: false,
      datesDirty: false,
      selectedFilters: {
        ...initialState.selectedFilters,
        primary_categories: state.filters.primary_categories,
        secondary_categories: state.filters.secondary_categories,
        pricing_options: state.filters.pricing_options,
        only_challengers: state.filters.toggles.onlyChallengers.default,
        group_by: secondaryCategory(state.currentUser.useSecondaryCategories)
      }
    }),
    [UPDATE_PRIMARY_CATEGORY_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        primary_categories: [...action.payload]
      }
    }),
    [UPDATE_SECONDARY_CATEGORY_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        secondary_categories: [...action.payload]
      }
    }),
    [UPDATE_PRICING_OPTION_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        pricing_options: [...action.payload]
      }
    }),
    [UPDATE_PERIOD_TYPE]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        period_type: action.payload
      }
    }),
    [UPDATE_TABLE_VIEW_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        table_view: action.payload
      }
    }),
    [UPDATE_ONLY_CHALLENGERS_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        only_challengers: action.payload
      }
    }),
    [UPDATE_EXCLUDE_MEMBERSHIP_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        exclude_membership: action.payload
      }
    }),
    [UPDATE_EXCLUDE_ZERO_SESSIONS_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        exclude_zero_sessions: action.payload
      }
    }),
    [SET_CHART_STATUS]: (state, action) => {
      switch (state.chartInfo.status) {
        case "idle":
          return chartIdleStatusReducer(state, action);
        case "loading":
          return chartLoadingStatusReducer(state, action);
        case "loaded":
          return chartLoadedStatusReducer(state, action);
        default:
          return state;
      }
    }
  },
  initialState
);

function chartIdleStatusReducer(state, action) {
  switch (action.payload) {
    case "CHECK":
      return checkReducer(state);
    case "UPDATE":
      return updateReducer(state);
    default:
      return state;
  }
}

function chartLoadingStatusReducer(state, action) {
  switch (action.payload) {
    case "SUCCESS":
      return { ...state, chartInfo: { ...state.chartInfo, status: "loaded", error: "" } };
    default:
      return state;
  }
}

function chartLoadedStatusReducer(state, action) {
  switch (action.payload) {
    case "CHECK":
      return checkReducer(state);
    case "UPDATE":
      return updateReducer(state);

    default:
      return state;
  }
}

function checkReducer(state) {
  if (state.selectedFilters.pricing_options.length <= 1000) {
    return { ...state, chartInfo: { ...state.chartInfo, status: "loading", error: "" } };
  }
  return {
    ...state,
    chartInfo: {
      ...state.chartInfo,
      status: "idle",
      error: "Please, select up to 1000 maximum sales packages, to load the chart"
    }
  };
}

function updateReducer(state) {
  if (
    state.selectedFilters.group_by.name == "Sales Package" &&
    state.selectedFilters.pricing_options.length > 50 &&
    state.selectedFilters.period_type == "Daily"
  )
    return {
      ...state,
      chartInfo: {
        ...state.chartInfo,
        error: "Please, select up to 50 maximum sales packages, to update the chart"
      }
    };
  else if (state.selectedFilters.pricing_options.length <= 1000) {
    return { ...state, chartInfo: { ...state.chartInfo, status: "loading", error: "" } };
  }
  return {
    ...state,
    chartInfo: {
      ...state.chartInfo,
      error:
        state.chartInfo.status !== "idle"
          ? "Please, select up to 1000 maximum sales packages, to update the chart"
          : state.chartInfo.error
    }
  };
}
